<template>
    <div>
        <b-row>
            <b-col cols="12">
                <n-button v-if="showReportProblemButton" v-b-modal.report-problem v-b-tooltip.hover title="Create"
                    type="success">
                    {{ prescriptionsSelected.length == 0 ? 'Create a Patient Case' : `Report a problem with
                    ${prescriptionsSelected.length} Script${(prescriptionsSelected.length > 1 ? 's' : '')}` }}
                </n-button>
                <n-button v-if="showReportProblemButton" v-b-modal="'rx-suppress'" v-b-tooltip.hover
                    title="Pause Reminders" :disabled="(prescriptionsSelected.length == 0)" type="success">
                    Pause Refill Reminders
                </n-button>
                <b-button v-if="showProccessQueueButton" @click="openBatch" variant="success"
                    :disabled="disableBatchRefill">
                    {{ batchModalTitle }}
                </b-button>
                <b-button @click="transferOut" v-if="showTransferButton" v-b-modal.rx-transfer-modal variant="warning"
                    :disabled="disableTransfer">
                    Transfer Out
                </b-button>
            </b-col>
            <b-col v-if="showVoidBox && !alwaysShowVoid">
                <checkbox inline v-model="showVoidRxs">Show Void & Expired ({{ voidAndExpiredCount }})</checkbox>
            </b-col>
        </b-row>

        <b-row v-if="showOnlineOrderConflictAlert" class="mt-3">
            <b-col>
                <b-alert variant="danger" :show="showOnlineOrderConflictAlert">
                    <i class="fas fa-exclamation-triangle"></i>
                    One of selected prescriptions must be processed from the online order queue first: Rx #{{
                        rxOnUnprocessedOrder }}
                </b-alert>
            </b-col>
        </b-row>

        <b-row>
            <b-col>
                <b-table id="prescription-history-table" striped responsive="true" sort-icon-left
                    :items="prescriptionsToShow" :fields="DetermineFieldsByMode(mode)" :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc" :tbody-tr-class="colorCodeTable" show-empty :empty-text="_tableEmptyText"
                    :busy="isLoading" :sort-compare="customSort" :no-local-sorting="(manualSort?.length ?? 0) > 0"
                    v-model="renderedItems" @sort-changed="$emit('sort-changed', $event)" @row-clicked="selectRow">
                    <template #table-busy>
                        <div class="text-center my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                    </template>
                    <template #cell(hasAddons)="data" v-if="showAddOns">
                        <p class="h5 mb-2" style="text-align: center">
                            <b-icon v-b-tooltip.hover.html="getAddOns(data.item.addOnsList)" v-if="data.item.hasAddons"
                                icon="patch-plus" />
                        </p>
                    </template>
                    <template #cell(nextProcessDate)="data">
                        <b v-if="moment().isBefore(data.value)">{{ data.value }}</b>
                        <div v-else>{{ data.value }}</div>
                    </template>
                    <template #cell(select)="data" v-if="showSelectCheckox">
                        <input v-if="!singleSelect" type="checkbox" :value="data.item" v-model="prescriptionsSelected"
                            inline />
                        <input v-else type="radio" :value="data.item" @change="prescriptionsSelected = [data.item]"
                            :checked="prescriptionsSelected.includes(data.item)" inline />
                    </template>
                    <template #cell(patientName)="data">
                        <div v-if="data.value.length > 0">
                            {{ data.value }}
                        </div>
                        <div v-else>
                            {{ data.item.patientLastName }}, {{ data.item.patientFirstName }}
                        </div>
                    </template>
                    <template #cell(rxId)="data">
                        <component :is="rxIdLink ? 'RouterLink' : 'span'" :to="{
                            name: 'PrescriptionDetailsId'
                            , params: {
                                rxId: data.item.rxId,
                            }
                        }">
                            <span v-if="data.item.isRefillableStatus" v-b-tooltip.hover
                                :title="`Memo: ${data.item.memo ?? ''}`">
                                {{ data.item.rxId }}
                            </span>
                            <b v-else v-b-tooltip.hover
                                :title="`Memo: ${data.item.memo ?? ''} \nStatus: ${data.item.statusName}`"
                                style="cursor: pointer;">
                                {{ data.item.rxId }}
                            </b>
                        </component>
                    </template>
                    <template #cell(qtyLeft)="data">
                        {{ Number((data.item.totalAllowedToDispense - data.item.totalDispensed).toFixed(2)) }}
                    </template>
                    <template #cell(fillDate)="data">
                        <b v-if="isFuture(data.value)" v-b-tooltip.hover title="Queued for future date">
                            {{ data.value }}
                        </b>
                        <span v-else>
                            {{ data.value }}
                        </span>
                    </template>
                    <template #cell(patientPay)="data">
                        {{ data.value | currency }}
                    </template>
                    <template #cell(promisedDate)="data">
                        <PromisedTimeSummary v-if="data.item.promisedDate" :value="data.item.promisedDate" icon-only />
                    </template>
                    <template #cell(rxIdWithExpirationWarning)="data">
                        <component :is="rxIdLink ? 'RouterLink' : 'span'" :to="{
                            name: 'PrescriptionDetailsId'
                            , params: {
                                rxId: data.item.rxId,
                            }
                        }">
                            {{ data.item.rxId }}
                        </component>
                        <b-badge v-if="moment(data.item.expirationDate).diff(moment(), 'days') <= 7" variant="danger">
                            <i class="fa fa-warning"></i>
                            Expires
                            {{ data.item.expirationDate | formatDate('L', false) }}
                        </b-badge>
                    </template>
                    <template #head(promisedDate)>
                        <i class="fa fa-clock" title="Promised" v-b-tooltip.hover />
                    </template>
                    <template #cell(edit)="data">
                        <div class="table-icon-actions">
                            <router-link :to="getPrescriptionDetailsRoute(data.item)" v-if="showAction('edit')"
                                class="mr-1">
                                <b-icon icon="pencil-square" v-b-tooltip.hover title="Edit" />
                            </router-link>
                            <a href="#" @click.prevent="openSubscriptionForm(data.item)"
                                v-if="showSubscribeButton(data.item)">
                                <i class="fa fa-credit-card" aria-hidden="true" v-b-tooltip.hover
                                    title="Subscribe to Auto-Refill" />
                            </a>
                            <a href="#" @click.prevent="openUnsuppressForm(data.item)"
                                v-if="showSuppressButton(data.item)">
                                <i class="fa fa-phone-slash" aria-hidden="true" v-b-tooltip.hover
                                    :title="getSuppressionTitle(data.item)" />
                            </a>
                            <b-icon v-if="showAction('delete') && !disabled" icon="x-circle-fill" v-b-tooltip.hover
                                title="Remove" variant="danger" style="cursor: pointer"
                                @click="remove(data.item)"></b-icon>
                            <router-link v-if="showAction('verify')" :to="getRouteWithRxArgument('PV2', data.item)">
                                <i class="fas fa-clipboard-check" v-b-tooltip.hover :title="tooltipText(data.item)"
                                    :class="getVerifyActionButtonsClass(data.item)" style="cursor: pointer"></i>
                            </router-link>
                            <router-link v-if="showAction('pv1')" :to="getRouteWithRxArgument('PV1', data.item)">
                                <i class="fas fa-clipboard-check" v-b-tooltip.hover :title="tooltipText(data.item)"
                                    :class="getVerifyActionButtonsClass(data.item)" style="cursor: pointer"></i>
                            </router-link>
                            <b-button v-if="showAction('process-autorefill')" variant="success"
                                @click="openBatch([data.item])" :disabled="disabled || patient?.deceased" size="sm">
                                Process
                            </b-button>
                        </div>
                    </template>
                    <template v-if="showRowDetails" #row-details="row">
                        <div class="px-4">
                            <div>
                                Directions: {{ row.item.directions }}
                            </div>
                        </div>
                    </template>
                    <template #cell(locked)="data">
                        {{ data.item.lockedBy }}
                        <div v-if="data.item.lockExpires">
                            Lock Expires {{ formatLockExpires(data.item.lockExpires) }}
                        </div>
                    </template>
                </b-table>
            </b-col>
        </b-row>

        <!-- PROBLEM CASES MODAL -->
        <b-row>
            <b-modal id="report-problem" size="xl" title="Create New Case" ok-title="Save Case"
                @show="handleReportProblemModalShow" @ok="saveCase">
                <b-row v-if="prescriptionsSelected.length">
                    <b-col>
                        <b-table id="prescription-selected-table" :items="prescriptionsSelected" :fields="rxFields"
                            striped responsive sort-icon-left>
                            <template #cell(qtyLeft)="data">
                                {{ data.item.totalAllowedToDispense - data.item.totalDispensed }}
                            </template>
                            <template #cell(patientPay)="data">
                                {{ data.value | currency }}
                            </template>
                        </b-table>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-form-select v-model="selectedProblem" :options="problemOptions" />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <label><strong>Notes:</strong></label>
                        <b-form-textarea style="max-height:100%" rows="9" v-model="problemNote" />
                    </b-col>
                </b-row>

                <b-row class="mt-3">
                    <b-col>
                        <CasesWatcherComponent v-model="caseWatchers" label="Case Watchers" :disabled="false"
                            :show-add-me-link="true">
                            <template #help-text>
                                <HelpPopup>
                                    Select users and teams to watch this case
                                </HelpPopup>
                            </template>
                        </CasesWatcherComponent>
                    </b-col>
                </b-row>

                <b-row class="mt-3">
                    <b-col>
                        <hr>
                        <b-form-group label="Question Set:" class="mb-3">
                            <b-select v-model="selectedCaseQuestionSetId" :options="caseQuestionSetOptions" />
                        </b-form-group>
                        <TaskQuestions ref="taskQuestionsComponent"
                            v-if="selectedCaseQuestionSetId === 0 || selectedCaseQuestionSetId"
                            :questions="caseQuestions" @input="updateCaseQuestions" :answers="caseAnswers"
                            :editing="selectedCaseQuestionSetId === 0" :show-errors="showCaseQuestionErrors" />
                    </b-col>
                </b-row>
            </b-modal>
        </b-row>

        <!-- REFILLS BATCH MODAL-->
        <b-modal id="rx-modal" size="xl" :title="batchModalTitle" hide-footer no-close-on-backdrop>
            <RxPage :refillBatchMode="refillBatchMode" :prescriptionsInBatch="rxForBatch" @batchFinished="batchFinished"
                :card="false">
            </RxPage>

            <hr>

            <b-button variant="dark" @click="batchFinished()">
                Cancel
            </b-button>
        </b-modal>
        <b-modal id="rx-unsuppress" title="Resume Reminders?" hide-footer>
            <p>Are you sure you want to resume reminders on this prescription?</p>
            <b-button @click="confirmUnsuppress" variant="success">Yes</b-button>
        </b-modal>
        <b-modal id="rx-suppress" title="Pause Reminders?" hide-footer>
            <v-select v-model="suppressReason" :options="suppressReasons">
            </v-select>
            <p>Are you sure you want to pause reminders on these prescriptions?</p>
            <b-button @click="confirmSuppress" variant="success">Yes</b-button>
        </b-modal>
        <!-- RX SUBSCRIPTION MODAL-->
        <b-modal id="rx-subscription" size="xl" title="Subscription to Auto-Refill" hide-footer>
            <RXSubscriptionForm :rx="rxToSubscribe" @saved="closeSubscriptionModal"></RXSubscriptionForm>
        </b-modal>
        <b-modal id="rx-transfer-modal" hide-footer title="Rx Transfer" size="lg" @shown="showRxTransfer">
            <RxTransferModalComponent id="rxTransferComponent" ref="rxTransferComponent" :patient="patient"
                @complete="$bvModal.hide('rx-transfer-modal')" />
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Component, Prop, PropSync, Ref, Vue, Watch } from 'vue-property-decorator';
import { Table, TableColumn } from "element-ui";
import axios from 'axios';
import moment from "moment";
import { BvModalEvent } from 'bootstrap-vue';
import { Prescription, RefillStatusCode } from '@/models/Prescription';
import { OrderOTCItem } from '@/models/Order';
import { CaseNote, CaseWatcher } from '@/models/Case';
import { RxTransferContents } from '@/models/RxTransfer';
import { Patient } from '@/models/Patient';
import { UserTaskQuestion, UserTaskAnswer, UserTaskQuestionSet, UserTaskQuestionType } from '@/models/UserTask';
import { EntityType } from '@/models/EntityType';
import SearchComponent from '@/components/SearchComponent.vue';
import RXSubscriptionForm from '@/components/Prescription/RXSubscriptionForm.vue';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import RxTransferModalComponent from "@/components/RxTransfer/RxTransferModalComponent.vue";
import PromisedTimeSummary from "@/components/Order/PromisedTimeSummaryComponent.vue";
import CasesWatcherComponent from "@/components/Common/CasesWatcherComponent.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import HelpPopup from '@/components/HelpPopup.vue';
import { Permission } from "@/models/Permission";

@Component({
    name: "PrescriptionsListComponent",
    components: {
        RxTransferModalComponent,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        //Importing this way prevents component circular reference
        RxPage: () => import('@/pages/Prescription/PrescriptionDetailsPage.vue'),
        SearchComponent, Checkbox,
        RXSubscriptionForm,
        PromisedTimeSummary,
        TaskQuestions: () => import("@/components/Tasks/TaskQuestions.vue"),
        CasesWatcherComponent,
        HelpPopup
    }
})
export default class PrescriptionsListComponent extends Vue {
    @Prop({ default: null }) private prescriptions!: Array<Prescription> | null;
    @Prop({ default: null }) private selected!: Array<Prescription> | null;
    @Prop({ default: false, type: Boolean }) protected hideActionsColumn!: boolean;
    @Prop({ default: false, type: Boolean }) private colorCodePrescriptions!: boolean;
    @Prop({ default: false, type: Boolean }) protected disabled!: boolean;
    @Prop({ default: false, type: Boolean }) protected showReportProblemButton!: false;
    @Prop({ default: false, type: Boolean }) protected showProccessQueueButton!: false;
    @Prop({ default: false, type: Boolean }) private showPatientName!: boolean;
    @Prop({ default: false, type: Boolean }) private showAddOns!: boolean;
    @Prop({ default: false, type: Boolean }) protected isLoading!: boolean;
    @Prop({ default: false, type: Boolean }) protected showTransferButton!: boolean;
    @Prop({ default: false, type: Boolean }) private alwaysShowVoid!: boolean;
    @Prop({ default: true, type: Boolean }) protected showSelectCheckox!: boolean;
    @Prop({ default: false, type: Boolean }) protected singleSelect!: boolean;
    @Prop({ default: true, type: Boolean }) protected showVoidBox!: boolean;
    @Prop({ default: null, type: (Function || null) }) protected customSort!: Function | null;

    @Prop({ default: "New Refills In Batch", type: String }) protected batchModalTitle!: string;
    @Prop({ default: false, type: Boolean }) private refillBatchMode!: boolean;
    @Prop() protected processingButtonCallback!: Function;
    @Prop() private colorCodes?: Function;

    @Prop() private orderID!: number;
    @Prop() private actionButtons!: Array<string>;
    @Prop() private mode!: string;
    @Prop() private tableEmptyText!: string;
    @Prop() private additionalFields!: Array<any>;
    @Prop() private fieldsKeysToRemove!: Array<any>;
    @Prop() private fieldsToRename!: Array<any>;
    @Prop() private defaultSort!: string;
    @Prop() private defaultDesc!: boolean;
    @Prop() protected patient!: Patient;

    @Prop() private manualSort?: Array<string>;
    @Prop({ default: false }) private rxIdLink!: boolean;
    @Prop({ default: false }) private showRowDetails!: boolean;

    @Ref() private rxTransferComponent!: RxTransferModalComponent;
    protected renderedItems: any[] = [];

    @PropSync('currentSort', { type: String, default: "fillDate" }) protected sortBy!: string;
    @PropSync('currentDesc', { type: Boolean, default: true }) protected sortDesc!: boolean;

    protected rxToSubscribe: Prescription = new Prescription();
    private rxToUnsuppress: Prescription = new Prescription();

    moment = moment;

    mounted() {
        this.updateSort();

        if (this.showRowDetails && this.renderedItems.length) {
            this.$set(this.renderedItems[0], '_showDetails', true);
        }
    }

    @Watch('defaultSort')
    @Watch('defaultDesc')
    updateSort() {
        if (this.defaultSort) {
            this.sortBy = this.defaultSort;
        }

        // noinspection PointlessBooleanExpressionJS
        if (this.defaultDesc != null && this.defaultDesc != undefined) {
            this.sortDesc = this.defaultDesc;
        }
    }

    protected isSortable: boolean = true;
    private showVoidRxs: boolean = false;
    protected rxForBatch: Array<Prescription> | null = [];
    private prescriptionsSelected: Prescription[] = [];
    private selectedProblem = null;
    private problemNote = "";
    private caseWatchers: CaseWatcher[] = [];
    private suppressReason = "";
    private hasOnlineOrderConflicts: boolean = false;
    private rxOnUnprocessedOrder: string = "";
    protected suppressReasons: string[] = ['No Longer Taking', 'Too Much On Hand', 'Too Expensive', 'Other'];

    get isManualSort() {
        return !!this.manualSort?.length;
    }

    get disableBatchRefill() {
        if (this.patient?.deceased) return true;
        if (this.prescriptionsSelected.length == 0) return true;
        if (!this.prescriptionsSelected.every(rx => !rx.isExpired && !rx.isVoid)) return true;
        return false;
    }

    get showOnlineOrderConflictAlert() {
        return this.hasOnlineOrderConflicts;
    }

    get disableTransfer() {
        if (this.prescriptionsSelected.length == 0) return true;
        if (!this.prescriptionsSelected.every(rx => !rx.isExpired && !rx.isVoid)) return true;
        return false;
    }

    protected problems: string[] = [];

    get problemOptions() {
        return [
            { value: null, text: 'Select A Problem' },
            ...this.problems
        ];
    }

    async getCaseProblems() {
        try {
            const resp = await axios.get<string[]>("/Case/problems");
            this.problems = resp.data;
        } catch (err) {
            console.error(err);
        }
    }

    get rxFields(): any[] {
        const addFieldKey = "_internal_add_field";

        let fields: any[] = [
            { key: "select", label: "", sortable: false },
            { key: "hasAddons", label: "Adds", sortable: true, class: this.showAddOns ? "" : "d-none" },
            { key: "productNameShort", label: "Drug", sortable: this.isManualSort ? this.manualSort?.includes('productNameShort') : true },
            { key: "patientName", label: "Patient", sortable: this.isManualSort ? this.manualSort?.includes('patientName') : false, class: this.showPatientName ? "" : "d-none" },
            { key: "storeName", label: "Store", sortable: this.isManualSort ? this.manualSort?.includes('storeName') : false },
            { key: "rxId", label: "Rx Id", sortable: this.isManualSort ? this.manualSort?.includes('rxId') : false },
            { key: "daySupply", label: "DS", sortable: this.isManualSort ? this.manualSort?.includes('daySupply') : false, tdClass: "text-center" },
            { key: "dispensed", label: "Dispensed", sortable: this.isManualSort ? this.manualSort?.includes('dispensed') : false, tdClass: "text-center" },
            { key: "qtyLeft", label: "Quantity Left", sortable: false, class: "text-center", },
            { key: "fillDate", label: "Date", sortable: this.isManualSort ? this.manualSort?.includes('fillDate') : true, sortDirection: "desc", formatter: this.dateFormatter },
            { key: "patientPay", label: "Patient Pay", sortable: this.isManualSort ? this.manualSort?.includes('patientPay') : false, },
            { key: "programName", label: "Program", sortable: this.isManualSort ? this.manualSort?.includes('programName') : true },
            { key: "edit", label: "", class: "edit-col" },
        ];

        if (this.fieldsKeysToRemove?.length) {
            fields = fields.filter(col => !this.fieldsKeysToRemove.some(key => key == col.key));
        }

        if (this.fieldsToRename?.length) {
            this.fieldsToRename.forEach(ren => {
                let field = fields.find(col => col.key == ren.key);
                if (field) field.label = ren.label;
            });
        }

        if (this.additionalFields?.length) {
            const editCol = fields.find(col => col.key == 'edit');
            fields = fields.filter(col => col.key != 'edit');

            const addFields = this.additionalFields.map(af => {
                const addField = { ...af };
                addField.field[addFieldKey] = true;
                return addField;
            });

            const fieldsWithIndex = addFields.filter(col => col.index != null);
            if (fieldsWithIndex?.length) {
                fieldsWithIndex.forEach(col => {
                    fields.splice(col.index + 1, 0, col.field);
                });
            }

            const fieldsWithNoIndex = addFields.filter(col => col.index == null).map(col => col.field);
            if (fieldsWithNoIndex?.length) {
                fields = fields.concat(fieldsWithNoIndex);
            }

            fields.push(editCol);
        }

        return fields;
    }

    private incompleteAnswersFields: any[] = [
        { key: "patientName", label: "Patient", sortable: false },
        { key: "patientGender", label: "Gender", sortable: true, },
        { key: "rma", label: "RMA #", sortable: true },
        { key: "ipledge", label: "IPledge #" },
        { key: "dispenseBeforeDate", label: "Dispense before Date" },
        { key: "productNameShort", label: "Drug", sortable: true },
        { key: "storeName", label: "Store", sortable: false },
        // { key: "rxWorkflowStatus", label: "Rx Status", sortable: true },
        { key: "orderCreated", formatter: this.dateFormatter, sortable: true, },
        { key: "rxId", label: "Rx Id", sortable: false },
        {
            key: "fillDate",
            label: "Fill Date",
            sortable: true,
            sortDirection: "desc",
            formatter: this.dateFormatter
        },
        { key: "patientPay", label: "Patient Pay", sortable: false, },
        { key: "programName", label: "Program", sortable: true },
        { key: "edit", label: "", },
    ];

    get voidAndExpiredCount(): number {
        return (this.prescriptions?.length ?? 0) - this.prescriptionsToShow.length;
    }

    created() {
        this.isSortable = this.mode != 'print';
    }


    get selectedWithNoRemainingQuantity(): boolean {
        for (let i = 0; i < this.prescriptionsSelected.length; i++) {

            let rx = this.prescriptionsSelected[i];
            console.log("rx remainig: ", rx.rxID, rx.totalAllowedToDispense - rx.totalDispensed);
            if (rx.totalAllowedToDispense - rx.totalDispensed <= 0) {
                return true;
            }
        }
        return false;
    }

    DetermineFieldsByMode(mode: string) {
        if (mode == "IncompleteAnswers") {
            return this.incompleteAnswersFields;
        } else {
            return this.rxFields;
        }
    }

    get _tableEmptyText(): string {
        return this.tableEmptyText || "No Prescriptions to Show";
    }

    get actionSpecified() {
        return this.showAction('verify') || this.showAction('edit') || this.showAction('delete');
    }

    @Watch("sortDesc", { immediate: true })
    sortDirChange(_val: any, _oldVal: any) {
    }

    @Watch('prescriptions')
    changePrescriptions() {
        this.rxForBatch = [];
        this.prescriptionsSelected = [];
    }

    get prescriptionsToShow(): Array<Prescription> {
        return this.prescriptions?.map(rx => new Prescription(rx))
            .filter(rx => this.alwaysShowVoid || this.showVoidRxs || (!rx.isVoid && !rx.isExpired)) ?? [];
    }

    dateFormatter(value: Date, _key: string, _item: any) {
        const dt = moment.utc(value);
        if (dt.isValid()) return dt.format("L");
        return "";
    }

    showAction(action: string) {
        return this.actionButtons?.some(a => a == action);
    }

    remove(rxRow: Prescription) {
        this.$emit('prescriptionRemoved', rxRow);
    }

    getPrescriptionDetailsRoute(rxRow: Prescription) {
        return {
            name: 'PrescriptionDetails'
            , params: {
                storeID: rxRow.storeID,
                rxNumber: rxRow.rxNumber,
                rfNumber: (rxRow.rfNumber || 0)
            }
        };
    }

    getRouteWithRxArgument(routeName: string, rxRow: Prescription) {
        return {
            name: routeName
            , params: {
                orderID: this.orderID,
                storeID: rxRow.storeID,
                rxNumber: rxRow.rxNumber,
                rfNumber: (rxRow.rfNumber || 0)
            }
        };
    }

    getVerifyActionButtonsClass(rxRow: Prescription) {
        return {
            'text-info': (rxRow.pV2ID || 0) == 0,
            'text-success': (rxRow.pV2ID || 0) > 0,
            'text-warning': (rxRow.dispenseErrorId || 0) > 0
        };
    }

    colorCodeTable(rx: Prescription, type: any) {
        if (this.colorCodes) {
            return this.colorCodes(rx, type);
        }
        if (!rx || type !== 'row' || !this.colorCodePrescriptions)
            return;
        if (rx.status == RefillStatusCode.TransferOut)
            return 'table-info';
        else if (rx.status == RefillStatusCode.Void)
            return 'table-danger strikethrough';
        else if (rx.status == RefillStatusCode.Discontinued)
            return 'table-danger font-italic';
        else if (rx.isExpired) return 'table-danger';
        else if (!rx.isRefillableStatus)
            return 'table-dark';
        else if (moment(rx.fillDate).isAfter(moment(), 'day'))
            return 'table-warning';
    }

    isFuture(date: any) {
        return moment(date).isAfter(moment(), 'day');
    }

    tooltipText(rxRow: Prescription) {
        let text: string;
        if ((rxRow.dispenseErrorId || 0) > 0)
            text = 'Error reported';
        else if ((rxRow.pV2ID || 0) > 0)
            text = 'Verified';
        else
            text = 'Verify';

        return text;
    }

    async saveCase(e: BvModalEvent) {
        e.preventDefault();

        if (!this.selectedProblem) {
            this.$notification(NotificationOptions.error("You must select a problem type"));
            return;
        }

        this.showCaseQuestionErrors = false;

        const hasCaseQuestions = !!this.caseQuestions?.length;
        const canEditCaseQuestions = true;

        if (canEditCaseQuestions && hasCaseQuestions) {
            const questionsHaveErrors = this.caseQuestions.some(q => {
                if (!q.text?.trim()) return true;
                if (![UserTaskQuestionType.multipleChoiceMultiple, UserTaskQuestionType.multipleChoiceSingle].includes(q.typeId)) return false;
                if (q.options.some(o => !o.text?.trim())) return true;
                const uniqueTexts = new Set(q.options.map(o => o.text?.trim()));
                if (uniqueTexts.size !== q.options.length) return true;
                return;
            });
            if (questionsHaveErrors) {
                this.$nextTick(() => {
                    this.showCaseQuestionErrors = true;
                });
                this.$notification(NotificationOptions.error("Some questions have errors."));
                return;
            }
        }

        const answers = this.taskQuestionsComponent?.cleanAnswers();

        if (this.selectedCaseQuestionSetId) {
            if (this.taskQuestionsComponent?.answersHaveErrors()) {
                this.$nextTick(() => {
                    this.showCaseQuestionErrors = true;
                });

                this.$notification(NotificationOptions.error("There are errors on the completion question answers."));
                return;
            }
        }

        const newCaseNote: CaseNote | any = {
            id: 0,
            patientId: this.patient.id,
            problem: this.selectedProblem,
            error: false,
            note: this.problemNote,
            resolvedBy: 'false',
            acceptedBy: 'false',
            questionSetId: this.selectedCaseQuestionSetId
        };

        try {
            const response = await axios.post('/Case/', {
                caseProblem: newCaseNote,
                prescriptions: this.prescriptionsSelected,
                questions: this.caseQuestions,
                answers,
                watchers: this.caseWatchers
            });
            this.$notification(NotificationOptions.success(`Created a new case (id: ${response.data.id})`));
            this.caseWatchers = [];
            this.$bvModal.hide("report-problem");
        } catch (err) {
            console.warn(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    @Watch('selected')
    buttonSelect() {
        this.prescriptionsSelected = this.selected ?? [];
    }

    @Watch('prescriptionsSelected')
    inputSelectedRxs(newSelectedRxs: Array<Prescription>) {
        this.$emit('selection-changed', newSelectedRxs);
        this.checkForOnlineOrderConflicts();
    }

    isSamePrescriptionOrder(rx1: Prescription, rx2: Prescription) {
        return rx1.storeID == rx2.storeID && rx1.rxNumber == rx2.rxNumber &&
            rx1.rfNumber == rx2.rfNumber && rx1.orderId == rx2.orderId;
    }

    async checkForOnlineOrderConflicts() {
        try {
            if (this.prescriptionsSelected.length === 0 || !this.patient?.id) {
                this.hasOnlineOrderConflicts = false;
                return;
            }

            const response = await axios.get(`/PatientWeb/GetUnprocessed?patientId=${this.patient.id}`);
            const unprocessedOrders = response.data;

            const conflictingPrescriptions = this.findPrescriptionsInUnprocessedOrders(this.prescriptionsSelected, unprocessedOrders);

            this.hasOnlineOrderConflicts = conflictingPrescriptions.length > 0;
            this.rxOnUnprocessedOrder = conflictingPrescriptions.map(rx => rx.rxNumber).join(', ');
        } catch (error) {
            console.error('Error checking online orders for alert:', error);
            this.hasOnlineOrderConflicts = false;
        }
    }

    //Take only the max RfNumber from group of StoreId - RxNbr
    async openBatch(rxForBatch: Array<Prescription>) {
        // Check if the patient is deceased
        if (this.patient?.deceased) {
            this.$notification(NotificationOptions.error('Cannot create refills for deceased patients.'));
            return;
        }

        if (this.selectedWithNoRemainingQuantity) {
            // Show your alert here.
            this.$notification(
                NotificationOptions.notificationOptionsPreset(
                    'Cannot create a new batch refill, selected prescriptions have no refills available.',
                    NotificationOptions.NotificationTypes.warning));
            return;
        }

        // Check if any selected prescriptions are in unprocessed online orders
        if (this.patient?.id) {
            try {
                const response = await axios.get(`/PatientWeb/GetUnprocessed?patientId=${this.patient.id}`);
                const unprocessedOrders = response.data;

                // Get the prescriptions we're trying to batch
                const prescriptionsToCheck = this.prescriptionsSelected?.length ? this.prescriptionsSelected : (rxForBatch?.length ? rxForBatch : this.prescriptions);

                if (prescriptionsToCheck && prescriptionsToCheck.length > 0) {
                    const conflictingPrescriptions = this.findPrescriptionsInUnprocessedOrders(prescriptionsToCheck, unprocessedOrders);

                    if (conflictingPrescriptions.length > 0) {
                        this.hasOnlineOrderConflicts = true;
                        this.rxOnUnprocessedOrder = conflictingPrescriptions.map(rx => rx.rxNumber).join(', ');
                        return;
                    } else {
                        this.hasOnlineOrderConflicts = false;
                    }
                }
            } catch (error) {
                console.error('Error checking online orders:', error);
                // Continue with batch processing if the check fails - don't block users
            }
        }

        if (this.refillBatchMode) {
            if (this.prescriptionsSelected?.length) {
                const sameStoreRx = (rx: any, rx2: any) => {
                    return rx.storeID == rx2.storeID && rx.rxNumber == rx2.rxNumber;
                };

                const findMaxRfInStoreRxGroup = (rx1: Prescription, rx2: Prescription): Prescription => {
                    return (rx1.rfNumber || 0) > (rx2.rfNumber ?? 0) ? rx1 : rx2;
                };

                //Find the distinct StoreId - RxNumber combinations in the selection
                const distinctStoreRxList = this.prescriptionsSelected.filter((rx, i, rxs) => {
                    const i2 = rxs.findIndex(rx2 => sameStoreRx(rx, rx2));
                    return i2 == i;
                }).map(rx => {
                    return { storeID: rx.storeID, rxNumber: rx.rxNumber };
                });

                //Find the prescription with the max RfNumber for each distinct StoreId - RxNumber combination
                this.rxForBatch = distinctStoreRxList.map(ids => {
                    const group = this.prescriptionsToShow.filter(rx => sameStoreRx(rx, ids));
                    return Object.assign(new Prescription(), group.reduce(findMaxRfInStoreRxGroup));
                });
            } else {
                this.rxForBatch = rxForBatch;
            }
        } else {
            if (this.prescriptionsSelected?.length)
                this.rxForBatch = this.prescriptionsSelected;
            else if (rxForBatch?.length)
                this.rxForBatch = rxForBatch;
            else
                this.rxForBatch = this.prescriptions;
        }

        this.$bvModal.show('rx-modal');
    }

    batchFinished(rx?: Prescription) {
        this.$bvModal.hide('rx-modal');
        this.$emit('batchFinished', rx);
    }

    openSubscriptionForm(rx: Prescription) {
        this.rxToSubscribe = rx;
        this.$bvModal.show('rx-subscription');
    }

    openUnsuppressForm(rx: Prescription) {
        this.rxToUnsuppress = rx;
        this.$bvModal.show('rx-unsuppress');
    }

    closeSubscriptionModal() {
        this.$bvModal.hide('rx-subscription');
        this.$emit('newSubscriptionSaved');
    }

    showSubscribeButton(rx: Prescription) {
        return this.showAction('subscribe') && rx.isAvailableForAutorefill;
    }

    showSuppressButton(rx: Prescription) {
        return this.showAction('suppress') && rx.suppression != null;
    }

    getSuppressionTitle(rx: Prescription) {
        return `${rx.suppression.reason} on ${new Date(rx.suppression.created).toLocaleDateString(navigator.language, {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric'
        })}`;
    }

    confirmUnsuppress() {
        this.$bvModal.hide('rx-unsuppress');
        var supp = this.rxToUnsuppress.suppression;

        supp.active = false;

        axios.post("/Prescription/Suppression", supp).then((_response: any) => {
            this.$notification(
                NotificationOptions.successSaveNotificationPreset(
                    `Prescription reminders resumed for ${this.rxToUnsuppress.rxID}`
                )
            );
            this.$emit('batchFinished', this.rxToUnsuppress);
        }).catch(err => this.$notification(NotificationOptions.error(err)));
    }

    confirmSuppress() {
        this.$bvModal.hide('rx-suppress');
        var ctr = 0;
        this.prescriptionsSelected.forEach((rx: Prescription, _index: number, array: Prescription[]) => {
            var supp = {
                storeId: rx.storeID,
                rxNumber: rx.rxNumber,
                active: true,
                reason: this.suppressReason
            };
            axios.post("/Prescription/Suppression", supp).then((_response: any) => {
                this.$notification(
                    NotificationOptions.successSaveNotificationPreset(
                        `Prescription reminders paused for ${rx.rxID}`
                    )
                );
                ctr++;
                if (ctr == array.length) {
                    this.$emit('batchFinished', rx);
                }
            }).catch(err => this.$notification(NotificationOptions.error(err)));
        });
    }

    getAddOns(AddOnsList: OrderOTCItem) {
        if (AddOnsList != null) {
            let a = '<strong> Add Ons </strong>'
            Object.entries(AddOnsList).forEach(
                ([_key, value]) =>
                    a += '<br /> ' + value.otcItemName + ': ' + value.quantity + (value.quantity == 1 ? ' pc' : ' pcs')
            )
            return a;
        }
        return '';
    }

    showRxTransfer() {
        const contents = this.prescriptionsSelected.map(rx => {
            return { medicationName: rx.drugName, rxNumber: rx.rxNumber.toString() } as unknown as RxTransferContents
        });
        if (this.rxTransferComponent) this.rxTransferComponent.startingContents = contents;
    }

    transferOut() {
        return this.prescriptionsSelected;
    }

    selectRow(item: Prescription, _index: number, _event: Event) {
        if (this.showRowDetails) {
            this.$set(item, "_showDetails", !(item as any)._showDetails);
        }

        if (!this.showSelectCheckox) return;
        if (!item.isActive) return;
        const foundIdx = this.prescriptionsSelected.findIndex(value => value.rxID === item.rxID);
        if (this.singleSelect) {
            if (foundIdx === -1) {
                this.prescriptionsSelected = [item];
            } else {
                this.prescriptionsSelected = [];
            }
            return;
        }
        if (foundIdx === -1) {
            this.prescriptionsSelected.push(item);
        } else {
            this.prescriptionsSelected.splice(foundIdx, 1);
        }
    }

    protected selectedCaseQuestionSetId: number | null = null;
    protected caseQuestions: UserTaskQuestion[] = [];
    protected caseAnswers: UserTaskAnswer[] = [];
    protected showCaseQuestionErrors = false;
    protected caseQuestionSets: UserTaskQuestionSet[] = [];
    @Ref("taskQuestionsComponent") protected taskQuestionsComponent: any | null;

    get caseQuestionSetOptions() {
        const options = [
            { value: null, text: 'None' },
            ...this.caseQuestionSets.map(qs => ({ value: qs.id, text: qs.name }))
        ];

        // Only include "Custom" option if user has permission
        const hasCustomQuestionSetPermission = this.$permissions.some(
            (p: Permission) => p.permissionCodeName === 'Compliance/Create Custom Case Question Sets'
        );

        if (hasCustomQuestionSetPermission) {
            options.push({ value: 0, text: 'Custom' });
        }

        return options;
    }

    async getCaseQuestionSets() {
        try {
            const resp = await this.$http.get<UserTaskQuestionSet[]>(`/task/question-sets/${EntityType.Case}`);
            this.caseQuestionSets = resp.data.filter(qs => qs.name && qs.active);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    handleReportProblemModalShow() {
        this.getCaseProblems();
        this.getCaseQuestionSets();
        this.selectedCaseQuestionSetId = null;
        this.caseQuestions = [];
    }

    @Watch("selectedCaseQuestionSetId")
    async getQuestionSet() {
        try {
            if (!this.selectedCaseQuestionSetId || this.selectedCaseQuestionSetId === 0) {
                this.caseQuestions = [];
                return;
            }
            const resp = await this.$http.get<UserTaskQuestionSet>(`/task/question-set/${this.selectedCaseQuestionSetId}`);
            this.caseQuestions = resp.data.questions;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    @Watch("selectedProblem")
    handleSelectedProblemChange() {
        if (!this.selectedProblem) {
            this.selectedCaseQuestionSetId = null;
            return;
        }
        const defaultQuestionSet = this.caseQuestionSets.find(qs => qs.defaultScope === this.selectedProblem);
        this.selectedCaseQuestionSetId = defaultQuestionSet?.id ?? null;
    }

    updateCaseQuestions(updatedQuestions: any[]) {
        this.caseQuestions = updatedQuestions;
    }

    findPrescriptionsInUnprocessedOrders(prescriptions: Prescription[], unprocessedOrders: any[]): Prescription[] {
        return prescriptions.filter(rx =>
            unprocessedOrders.some(order =>
                order.prescriptions?.some((onlineRx: any) =>
                    onlineRx.storeId === rx.storeID && onlineRx.rxNumber === rx.rxNumber
                )
            )
        );
    }

    formatLockExpires(value: Date) {
        return moment.utc(value).fromNow();
    }
}

export class PrescriptionListColumn {
    public key?: string;
    public label?: string;
    public sortable?: boolean;
    public class?: string;
    public sortDirection?: string;
    public formatter?: Function;
}

</script>

<style scoped>
::v-deep(.tooltip-inner) {
    white-space: pre-wrap;
}

::v-deep(.strikethrough td) {
    text-decoration: line-through;
}

::v-deep(.edit-col) {
    width: 30px;
}
</style>
